import { i18n } from 'boot/i18n';
import { Timestamp } from 'firebase/firestore';
import { filter, find, includes, isEmpty } from 'lodash';
import { APPOINTMENT_INTEGRATION_FIELDS } from 'src/constants/appointments';
import AppointmentsDB from 'src/fb/appointments-db';
import GenericDb from 'src/fb/generic-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import { useTime } from 'src/composables/time';

// eslint-disable-next-line import/prefer-default-export
export function useAppointments() {
  const { adjustDate } = useTime();
  async function filterAppointmentsWithCorrectUser(appointments) {
    const sessionActivitiesDb = new SessionActivitiesDB();

    const actions = appointments.map(async (appointment) => {
      if (!appointment.sessionActivity) {
        return appointment;
      }

      const sessionActivity = await sessionActivitiesDb.read(appointment.sessionActivity);

      if (sessionActivity && appointment.user === sessionActivity.user) {
        return appointment;
      }

      return null;
    });

    const filteredAppointmentsBySessionActivityUser = await Promise.all(actions);

    return filteredAppointmentsBySessionActivityUser.filter((appointment) => appointment !== null);
  }

  /**
   * The day to look for linkable appointments in. Activities being added retroactively carry their
   * own start date; ones created on the spot may not have a timestamp yet.
   *
   * @param {Object} sessionActivity
   * @return {Date}
   */
  function appointmentLookupStartDate(sessionActivity) {
    return sessionActivity?.startTimestamp instanceof Timestamp
      ? sessionActivity.startTimestamp.toDate()
      : new Date();
  }

  /**
   * Load the appointments that can be linked to an activity for a given day.
   * Single source of truth for both the change-appointment modal and the
   * end-of-session gate that decides whether to open it at all.
   *
   * @param {Object} params
   * @param {string} params.clinic - clinic id
   * @param {string|null} params.integration - connected clinic integration
   * @param {string|null} params.clientId
   * @param {string|null} params.userId
   * @param {Date} params.startDate - the activity day; the query spans 00:00-23:59 of it
   * @param {string[]} [params.disableAppointments] - appointment ids to exclude
   * @param {string} [params.includeAppointmentId] - currently-linked appointment to keep in the list
   * @return {Promise<Object[]>}
   */
  async function loadLinkableAppointments({
    clinic,
    integration,
    clientId = null,
    userId = null,
    startDate,
    disableAppointments = [],
    includeAppointmentId = null,
  }) {
    if ((clientId === null) && (userId === null)) {
      return [];
    }

    const appointmentsDb = new AppointmentsDB();
    const startTimestamp = Timestamp.fromDate(
      adjustDate(startDate, { hour: 0, minute: 0, second: 0, millisecond: 0 }),
    );

    // not sure if we always have this field in other integrations, so apply this only for Aloha
    const clientField = integration === 'aloha' && clientId === null
      ? 'integrationClientParRef'
      : 'client';

    const query = [
      ['clinic', '==', clinic],
      [clientField, '==', clientId],
      ['user', '==', userId],
      ['status', '==', 'scheduled'],
      ['scheduledStartTimestamp', '>=', startTimestamp],
      ['scheduledStartTimestamp', '<=', Timestamp.fromDate(
        adjustDate(startDate, { hour: 23, minute: 59, second: 59, millisecond: 999 }),
      )],
    ];

    const appointments = filter(
      await appointmentsDb.readAll(query),
      (appointment) => isEmpty(appointment.recurringWeekdays)
        && (appointment?.type !== 'unavailability')
        && (!includes(disableAppointments, appointment.id))
        && !(appointment?.cancellationRequest?.status === 'pending'),
    );

    if (includeAppointmentId && (!find(appointments, { id: includeAppointmentId }))) {
      const currentAppointment = await appointmentsDb.read(includeAppointmentId);
      if (currentAppointment) {
        appointments.push(currentAppointment);
      }
    }

    // an appointment may have a session activity with a different user - filter those out
    return filterAppointmentsWithCorrectUser(appointments);
  }

  /**
   * Writes both sides of the link: the activity gets the appointment id and the integration fields,
   * the appointment gets claimed by the activity.
   *
   * @param {Object} sessionActivity
   * @param {string} appointmentId
   * @param {Object} [options]
   * @param {string|null} [options.sessionId] - null for staff-only activities, which have no session
   * @return {Promise<{updatedAppointment: Object, sessionActivityChanges: Object}>}
   * @throws when the appointment is missing, not scheduled or another user's - neither side is written
   */
  async function linkAppointmentToSessionActivity(sessionActivity, appointmentId, { sessionId = null } = {}) {
    const appointmentsDb = new AppointmentsDB();
    const sessionActivitiesDb = new SessionActivitiesDB();
    const genericDb = new GenericDb();

    return genericDb.executeTransaction(async (transactionMethods) => {
      const appointment = await transactionMethods.read(appointmentsDb, { id: appointmentId });

      // The list was filtered when it loaded; the modal can stay open for minutes while the PM system
      // changes the appointment. `set` merges into a missing document, recreating a deleted one.
      if (!appointment
        || (appointment.status !== 'scheduled')
        || (appointment.user !== sessionActivity.user)) {
        throw new Error(i18n.t('sessions.modals.changeSessionAppointment.notifyAppointmentNoLongerAvailable'));
      }

      const appointmentData = {};
      APPOINTMENT_INTEGRATION_FIELDS.forEach((field) => {
        if (field in appointment) {
          appointmentData[field] = appointment[field];
        }
      });

      const appointmentChanges = {
        sessionActivity: sessionActivity.id,
        status: sessionActivity.status === 'in-progress' ? 'in-progress' : 'completed',
        session: sessionId,
      };

      const sessionActivityChanges = {
        id: sessionActivity.id,
        appointment: appointmentId,
        appointmentData,
      };

      await transactionMethods.set(appointmentsDb, { data: appointmentChanges, id: appointmentId });
      await transactionMethods.update(sessionActivitiesDb, { data: sessionActivityChanges });

      return {
        updatedAppointment: { ...appointment, ...appointmentChanges },
        sessionActivityChanges,
      };
    });
  }

  /**
   * Only an in-progress appointment is moved, so one cancelled or completed elsewhere keeps its
   * status.
   *
   * @param {string|null} appointmentId
   * @return {Promise<void>}
   */
  async function completeLinkedAppointment(appointmentId) {
    if (!appointmentId) {
      return;
    }

    const appointmentsDb = new AppointmentsDB();
    const appointment = await appointmentsDb.read(appointmentId);

    if (appointment?.status !== 'in-progress') {
      return;
    }

    await appointmentsDb.set({ status: 'completed' }, appointmentId);
  }

  return {
    filterAppointmentsWithCorrectUser,
    appointmentLookupStartDate,
    loadLinkableAppointments,
    linkAppointmentToSessionActivity,
    completeLinkedAppointment,
  };
}
